import { ApiError } from '../../lib/apiClient';
import { getErrorMessage } from '../../lib/errors';
import type {
  AssistantActionCard,
  AssistantActionStatus,
  AssistantActionTier,
  AssistantResourceRef,
  QuotaRead,
} from '../../types/api';
import { isKnownResourceType } from './changeRefresh';

/** How long the thread waits for the follow-up a confirm promises. */
export const FOLLOWUP_GIVE_UP_MS = 90_000;

interface CardWording {
  /** The marker above the summary. */
  marker: string;
  /** The Confirm button, saying what it does. */
  confirm: string;
}

/**
 * Per tool: the tier marker and what Confirm says. C cards spend, so they read
 * "Uses a post"; O cards act outward or for good, so they say how.
 */
const WORDING: Readonly<Record<string, CardWording>> = {
  generate_post: { marker: 'Uses a post', confirm: 'Create post' },
  choose_caption: { marker: 'Uses a post', confirm: 'Use this caption' },
  revise_caption: { marker: 'Uses a post', confirm: 'Revise' },
  redesign_slides: { marker: 'Uses a post', confirm: 'Redesign' },
  regenerate: { marker: 'Uses a post', confirm: 'Regenerate' },
  retry_failed: { marker: 'Uses a post', confirm: 'Retry' },
  run_plan_now: { marker: 'Uses a post', confirm: 'Create post now' },
  approve_run: { marker: "Your approval · can't be undone", confirm: 'Approve' },
  publish_linkedin: {
    marker: "Posts to LinkedIn · can't be undone",
    confirm: 'Publish to LinkedIn',
  },
  set_plan_automation: { marker: 'Changes how a planned post goes out', confirm: 'Apply' },
  delete_product: { marker: "Deletes · can't be undone", confirm: 'Delete' },
  delete_voice_example: { marker: "Deletes · can't be undone", confirm: 'Delete' },
  delete_proof_point: { marker: "Deletes · can't be undone", confirm: 'Delete' },
  delete_key_date: { marker: "Deletes · can't be undone", confirm: 'Delete' },
  delete_planned_post: { marker: "Deletes · can't be undone", confirm: 'Delete' },
  delete_campaign: { marker: "Deletes · can't be undone", confirm: 'Delete' },
};

export function cardWording(card: AssistantActionCard): CardWording {
  const known = WORDING[card.tool];
  if (known) return known;
  if (card.tool.startsWith('delete_'))
    return { marker: "Deletes · can't be undone", confirm: 'Delete' };
  return card.tier === 'C'
    ? { marker: 'Uses a post', confirm: 'Confirm' }
    : { marker: "Acts for you · can't be undone", confirm: 'Confirm' };
}

function plural(count: number, word: string): string {
  return `${count} ${word}${count === 1 ? '' : 's'}`;
}

/**
 * The C card's cost line: "Uses 1 post · 48 of 50 left", with
 * "+ picture credit" when a picture renders. The allowance is the account's
 * pool from `QuotaProvider`; while it is unknown the line leaves it out.
 */
export function costLine(card: AssistantActionCard, quota: QuotaRead | null): string | null {
  if (card.tier !== 'C' || card.cost === null) return null;
  const { generations, picture_credit } = card.cost;
  const parts: string[] = [];
  if (generations > 0) {
    parts.push(`Uses ${plural(generations, 'post')}`);
  } else if (picture_credit) {
    parts.push('Uses a picture credit');
  } else {
    parts.push('Uses no posts');
  }
  if (generations > 0 && picture_credit) parts[0] += ' + picture credit';
  if (generations > 0 && quota !== null) parts.push(`${quota.remaining} of ${quota.limit} left`);
  return parts.join(' · ');
}

/** Where Open goes for a card's outcome: a run's review, or the calendar. */
export function outcomeLink(card: AssistantActionCard): string | null {
  const resource = card.result?.resource ?? null;
  if (resource === null || resource.id === null) return null;
  if (resource.type === 'run') return `/review/${resource.id}`;
  if (resource.type === 'planned_post') return '/calendar';
  return null;
}

/** The outcome in words, for the line and the live region. */
export function outcomeText(card: AssistantActionCard, status: AssistantActionStatus): string {
  switch (status) {
    case 'done':
      return card.result?.message || 'Done.';
    case 'failed':
      return card.result?.message || 'That did not go through.';
    case 'cancelled':
      return 'Cancelled. Nothing happened.';
    case 'expired':
      return 'This card expired. Ask again if you still want it.';
    case 'executing':
      return 'Working on it.';
    default:
      return '';
  }
}

/** A refused confirm or cancel in words: the server's own sentence for the 409s. */
export function cardFailureMessage(caught: unknown): string {
  if (caught instanceof ApiError && caught.status === 404) {
    return 'That card is no longer there.';
  }
  return getErrorMessage(caught, 'That did not go through. Try again.');
}

/** True when a refused confirm/cancel means the card on screen is out of date. */
export function isSettledCardError(caught: unknown): boolean {
  return (
    caught instanceof ApiError &&
    (caught.status === 404 ||
      caught.code === 'ASSISTANT_ACTION_NOT_PENDING' ||
      caught.code === 'ASSISTANT_ACTION_EXPIRED')
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function parseResource(value: unknown): AssistantResourceRef | null {
  if (!isRecord(value) || !isKnownResourceType(value.type)) return null;
  return { type: value.type, id: typeof value.id === 'string' ? value.id : null };
}

const TIERS = new Set<string>(['C', 'O'] satisfies AssistantActionTier[]);
const STATUSES = new Set<string>([
  'pending',
  'executing',
  'done',
  'failed',
  'cancelled',
  'expired',
] satisfies AssistantActionStatus[]);

/** One card, narrowed rather than cast: a stream frame is untrusted. */
function parseCard(value: unknown): AssistantActionCard | null {
  if (!isRecord(value)) return null;
  const { action_id, tool, tier, summary, cost, resource, status, result } = value;
  if (typeof action_id !== 'string' || !action_id) return null;
  if (typeof summary !== 'string' || !summary) return null;
  if (typeof tier !== 'string' || !TIERS.has(tier)) return null;
  if (typeof status !== 'string' || !STATUSES.has(status)) return null;
  return {
    action_id,
    tool: typeof tool === 'string' ? tool : '',
    tier: tier as AssistantActionTier,
    summary,
    cost:
      isRecord(cost) && typeof cost.generations === 'number'
        ? { generations: cost.generations, picture_credit: cost.picture_credit === true }
        : null,
    resource: parseResource(resource),
    status: status as AssistantActionStatus,
    result: isRecord(result)
      ? {
          message: typeof result.message === 'string' ? result.message : null,
          resource: parseResource(result.resource),
        }
      : null,
    expires_at: typeof value.expires_at === 'string' ? value.expires_at : '',
    created_at: typeof value.created_at === 'string' ? value.created_at : '',
  };
}

/** One SSE `action` frame: `{reply_to, action}`. */
export function parseActionEvent(
  data: unknown,
): { replyTo: string | null; action: AssistantActionCard } | null {
  if (typeof data !== 'string') return null;
  try {
    const parsed: unknown = JSON.parse(data);
    if (!isRecord(parsed)) return null;
    const action = parseCard(parsed.action);
    if (action === null) return null;
    return { replyTo: typeof parsed.reply_to === 'string' ? parsed.reply_to : null, action };
  } catch {
    return null;
  }
}
